export const LAYER_ROLES = [
  'skin',
  'outline',
  'shadow',
  'highlight',
  'whites',
  'pupilColour',
  'pupilDetail',
  'pupilReflection',
  'hairBack',
  'hairBackShadow',
  'hairBackHighlight',
  'hairBackOutline',
] as const

export type LayerRole = (typeof LAYER_ROLES)[number]

export const SUFFIX_TO_ROLE: ReadonlyArray<readonly [string, LayerRole]> = [
  ['_whites', 'whites'],
  ['_O', 'outline'],
  ['_SD', 'shadow'],
  ['_HL', 'highlight'],
  ['_PC', 'pupilColour'],
  ['_PD', 'pupilDetail'],
  ['_PR', 'pupilReflection'],
  ['_BKSD', 'hairBackShadow'],
  ['_BKHL', 'hairBackHighlight'],
  ['_BKO', 'hairBackOutline'],
  ['_BK', 'hairBack'],
]

export const ROLE_LABELS: Readonly<Record<LayerRole, string>> = {
  skin: 'Base artwork',
  outline: 'Outline',
  shadow: 'Shadow',
  highlight: 'Highlight',
  whites: 'Eye whites',
  pupilColour: 'Pupil colour',
  pupilDetail: 'Pupil detail',
  pupilReflection: 'Pupil reflection',
  hairBack: 'Behind the head',
  hairBackShadow: 'Behind the head — shadow',
  hairBackHighlight: 'Behind the head — highlight',
  hairBackOutline: 'Behind the head — outline',
}

const BASE = /^[A-Za-z0-9][A-Za-z0-9._-]*$/

export interface ParsedLayer {
  ok: true
  filename: string
  base: string
  role: LayerRole
}

export interface UnreadableLayer {
  ok: false
  filename: string
  code: 'bad_layer_name'
  detail: string
}

export type LayerNameResult = ParsedLayer | UnreadableLayer

const unreadable = (filename: string, detail: string): UnreadableLayer => ({
  ok: false,
  filename,
  code: 'bad_layer_name',
  detail,
})

export function parseLayerFilename(filename: string): LayerNameResult {
  if (!filename.toLowerCase().endsWith('.png')) {
    return unreadable(
      filename,
      `“${filename}” is not a PNG. Upload the PNG the artist exported — the studio makes the other formats itself.`,
    )
  }

  const stem = filename.slice(0, -'.png'.length)
  if (stem === '') {
    return unreadable(filename, `“${filename}” has no name before “.png”.`)
  }

  for (const [suffix, role] of SUFFIX_TO_ROLE) {
    if (!stem.endsWith(suffix)) {
      continue
    }
    const base = stem.slice(0, -suffix.length)
    if (base === '') {
      return unreadable(
        filename,
        `“${filename}” is only the “${suffix}” ending, with no option name in front of it.`,
      )
    }
    if (!BASE.test(base)) {
      return unreadable(filename, badBaseDetail(filename, base))
    }

    return { ok: true, filename, base, role }
  }

  if (!BASE.test(stem)) {
    return unreadable(filename, badBaseDetail(filename, stem))
  }

  return { ok: true, filename, base: stem, role: 'skin' }
}

function badBaseDetail(filename: string, base: string): string {
  return `“${filename}” has an unusable name (“${base}”). Use letters, digits, dot, dash or underscore, starting with a letter or digit.`
}

export interface LayerSetReview {
  base: string | null
  layers: ParsedLayer[]
  problems: UnreadableLayer[]
  mixedBases: string[]
  missingBase: boolean
  duplicateRoles: LayerRole[]
}

export function reviewLayerFiles(filenames: string[]): LayerSetReview {
  const parsed = filenames.map(parseLayerFilename)
  const layers = parsed.filter((p): p is ParsedLayer => p.ok)
  const problems = parsed.filter((p): p is UnreadableLayer => !p.ok)

  const bases = Array.from(new Set(layers.map((l) => l.base)))
  const counts = new Map<string, number>()
  layers.forEach((l) => counts.set(l.base, (counts.get(l.base) ?? 0) + 1))
  const base =
    bases.length === 0
      ? null
      : bases.reduce((best, b) =>
          (counts.get(b) ?? 0) > (counts.get(best) ?? 0) ? b : best,
        )

  const seen = new Map<LayerRole, number>()
  layers.forEach((l) => {
    if (l.base === base) {
      seen.set(l.role, (seen.get(l.role) ?? 0) + 1)
    }
  })

  return {
    base,
    layers,
    problems,
    mixedBases: bases.filter((b) => b !== base),
    missingBase: layers.length > 0 && !layers.some((l) => l.base === base && l.role === 'skin'),
    duplicateRoles: Array.from(seen.entries())
      .filter(([, n]) => n > 1)
      .map(([role]) => role),
  }
}

export function explainMixedBases(review: LayerSetReview): string | null {
  if (!review.mixedBases.length || !review.base) {
    return null
  }
  const strays = review.layers
    .filter((l) => l.base !== review.base)
    .map((l) => `“${l.filename}”`)
    .join(', ')

  return `${strays} name a different option than “${review.base}”. Rename them to start with “${review.base}”, or upload them as their own option.`
}
